import Vue from "vue";
import VueRouter from "vue-router";

Vue.use(VueRouter);

import { CheckPermission, RuleOneOf } from "@/plugins/permissions";

const routes = [
    {
        path: "/page/:slug",
        name: "Home",
        component: () => import("@/views/Page.vue"),
        meta: {
            type: "page",
            required_permission: new RuleOneOf([
                "drg.app.pages.view",
                "drg.org.pages.view"
            ])
        }
    },
    {
        path: "/form/:slug",
        name: "Form",
        component: () => import("@/views/Form.vue"),
        meta: {
            type: "form",
            required_permission: new RuleOneOf([
                "drg.app.forms.view",
                "drg.org.forms.view"
            ])
        }
    },
    {
        path: "/knowledge",
        name: "Knowledge",
        component: () => import("@/views/knowledge/Index"),
        meta: {
            type: "knowledge",
            required_permission: new RuleOneOf([
                "drg.app.knowledge.view",
                "drg.org.knowledge.view"
            ])
        }
    },
    {
        path: "/knowledge/search",
        name: "KnowledgeSearch",
        component: () => import("@/views/knowledge/Search.vue"),
        meta: {
            type: "knowledge",
            required_permission: new RuleOneOf([
                "drg.app.knowledge.view",
                "drg.org.knowledge.view"
            ])
        }
    },
    {
        path: "/knowledge/article/:slug",
        name: "KnowledgeArticle",
        component: () => import("@/views/knowledge/Article.vue"),
        meta: {
            type: "knowledge",
            required_permission: new RuleOneOf([
                "drg.app.knowledge.view",
                "drg.org.knowledge.view"
            ])
        }
    },
    {
        path: "/knowledge/:slug",
        name: "KnowledgeTaxon",
        component: () => import("@/views/knowledge/Taxon.vue"),
        meta: {
            type: "knowledge",
            required_permission: new RuleOneOf([
                "drg.app.knowledge.view",
                "drg.org.knowledge.view"
            ])
        }
    },
    {
        path: "/profile",
        name: "Profile",
        component: () => import("@/views/profile/Index.vue"),
        meta: {
            type: "profile",
            breadcrumb: "profile.title",
            required_permission: new RuleOneOf([
                "drg.app.reservations.view",
                "drg.app.profile.view",
                "drg.app.profile.address.view",
                "drg.app.profile.consents.view"
            ])
        }
    },
    {
        path: "/profile/invoices",
        name: "ProfileInvoices",
        component: () => import("@/views/profile/invoices/Index.vue"),
        meta: {
            type: "profile",
            breadcrumb: {
                parent: "Profile"
            },
            required_permission: "drg.org.pages.view"
        }
    },
    {
        path: "/profile/manage",
        name: "ManageProfile",
        component: () => import("@/views/profile/manage/Index.vue"),
        meta: {
            type: "profile",
            breadcrumb: {
                parent: "Profile"
            },
            required_permission: new RuleOneOf([
                "drg.app.profile.edit",
                "drg.app.profile.emails.edit",
                "drg.app.profile.phones.edit"
            ])
        }
    },
    {
        // alias, new route above
        // TODO arr meta redirect to and redirect isnide closure
        path: "/profile/consents/manage",
        name: "ConsentsManageOld",
        component: () => import("@/views/profile/manage/Consents.vue"),
        meta: {
            type: "profile",
            redirect_to: "ManageConsents"
        }
    },
    {
        path: "/profile/manage/consents/:consent_id?",
        name: "ManageConsents",
        component: () => import("@/views/profile/manage/Consents.vue"),
        meta: {
            type: "profile",
            breadcrumb: {
                parent: "Profile"
            },
            required_permission: "drg.app.profile.consents.edit"
        }
    },
    {
        path: "/profile/view/reservations",
        name: "ViewReservations",
        component: () => import("@/views/profile/manage/Reservations.vue"),
        meta: {
            type: "profile",
            breadcrumb: {
                parent: "Profile"
            },
            required_permission: "drg.app.reservations.edit"
        }
    },
    {
        path: "/login",
        name: "Login",
        component: () => import("@/views/LoginForm.vue"),
        meta: {
            type: "common",
            system_page: true
        }
    },
    {
        path: "/error/403",
        name: "Forbidden",
        component: () => import("@/views/error/Forbidden.vue"),
        meta: {
            type: "common",
            system_page: true
        }
    },
    {
        path: "/error/404",
        name: "Not Found",
        component: () => import("@/views/error/NotFound.vue"),
        meta: {
            type: "common",
            system_page: true
        }
    },
    {
        path: "/error/500",
        name: "Server Error",
        component: () => import("@/views/error/ServerError.vue"),
        meta: {
            type: "common",
            system_page: true
        }
    },
    {
        path: "/test-inputs",
        name: "Test Page",
        component: () => import("@/views/test/FormulateInputs.vue"),
        meta: {
            type: "test",
            system_page: true
        }
    }
];

const router = new VueRouter({
    base: process.env.BASE_URL,
    routes
});

const originalPush = router.push;
router.push = (location, onResolve, onReject) => {
    if (onResolve || onReject) {
        return originalPush.call(router, location, onResolve, onReject);
    }

    return originalPush.call(router, location).catch(err => {
        if (VueRouter.isNavigationFailure(err)) {
            return err;
        }

        return Promise.reject(err);
    });
};

router.beforeEach((to, from, next) => {
    if (to?.meta?.system_page) {
        return next();
    }
    if (to?.meta?.redirect_to) {
        return next({ name: to.meta.redirect_to });
    }

    console.debug('Router "beforeEach" data, ', to?.meta?.required_permission);
    if (!CheckPermission(to?.meta?.required_permission)) {
        return next("/error/403");
    } else {
        return next();
    }
});

router.afterEach(to => {
    document.body.setAttribute("data-view-type", "");
    if (to?.meta?.type) {
        document.body.setAttribute("data-view-type", to?.meta?.type);
    }
});

export default router;
